import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getEtoeContext, signEtoe } from '../api/jobs';
import type { EtoeContext } from '../types';
import { Spinner, ErrorBanner, toMessage } from '../components/common';
import { PublicBrand } from '../components/PublicBrand';
import { feeText } from '../format';

function formatDate(iso: string | null): string {
  if (!iso) return 'the agreed valuation date';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Public in-app eTOE (proposal) signing page. Renders the WA Business Valuations
// proposal from the job data and captures a typed-name digital signature.
export function Etoe() {
  const { token = '' } = useParams();
  const [ctx, setCtx] = useState<EtoeContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedName, setSignedName] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const c = await getEtoeContext(token);
      setCtx(c);
      if (c.alreadySigned) setSignedName(c.signerName ?? 'the authorised signatory');
    } catch (err) {
      setLoadError(toMessage(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please type your full name to sign.');
      return;
    }
    if (!accepted) {
      setError('Please tick the box to accept the terms.');
      return;
    }
    setBusy(true);
    try {
      await signEtoe(token, name.trim());
      setSignedName(name.trim());
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
          <ErrorBanner message={loadError} />
          <p className="muted">
            This proposal link may have expired or already been signed. Please contact your WA
            Business Valuations adviser for a new link.
          </p>
        </div>
      </div>
    );
  }
  if (!ctx) return <Spinner label="Loading your proposal." />;

  if (signedName) {
    return (
      <div className="public-wrap">
        <div className="card public-card">
          <PublicBrand />
          <h1>Proposal signed</h1>
          <p>
            Thank you, <strong>{signedName}</strong>. Your proposal for{' '}
            <strong>{ctx.entityName}</strong> has been accepted.
          </p>
          <p className="muted">
            We have emailed you a secure link to upload your documents so we can begin. If it does
            not arrive shortly, please check your junk folder or contact us.
          </p>
        </div>
      </div>
    );
  }

  // Payment terms: full fee, due within 7 days of our invoice. No partial payments.
  const balanceNote = 'Our fee is payable in full within 7 days of the issue of our invoice.';

  return (
    <div className="public-wrap etoe">
      <div className="card public-card" style={{ textAlign: 'center' }}>
        <PublicBrand />
      </div>

      <div className="card">
        <h1>Terms of Engagement</h1>
        <p className="muted">Proposal for {ctx.entityName}. Please review and sign at the bottom.</p>

        <h3>Scope of work</h3>
        <p>
          You have engaged us to conduct an Independent {ctx.valuationType} of {ctx.entityName} as at{' '}
          {formatDate(ctx.valuationDate)}. We will present our valuation in a written report.
        </p>
        {ctx.objective ? (
          <p>The objective of the valuation is {ctx.objective}.</p>
        ) : null}
        <p>
          The engagement is prepared under APES 225 Valuation Services issued by the Accounting
          Professional &amp; Ethical Standards Board. You are responsible for providing the
          information we request; we rely on its accuracy and completeness and our procedures do not
          include verifying it.
        </p>
        {ctx.financialYears.length > 0 ? (
          <p>
            <strong>Financial periods:</strong> {ctx.financialYears.join(', ')}.
          </p>
        ) : null}
        {ctx.scopeRestrictions ? (
          <p>
            <strong>Scope restrictions:</strong> {ctx.scopeRestrictions}.
          </p>
        ) : null}

        <h3>Price &amp; payment terms</h3>
        <table className="etoe-fees">
          <tbody>
            <tr>
              <td>Independent {ctx.valuationType} for {ctx.entityName}</td>
              <td>{feeText(ctx.feeQuotedExGst)}</td>
            </tr>
          </tbody>
        </table>
        <p>{balanceNote}</p>

        <h3>Your acceptance</h3>
        <p>
          By signing below you confirm you understand and agree to the nature of our services and the
          price and payment terms, and consent to receiving email correspondence from us.
        </p>

        {error ? <ErrorBanner message={error} /> : null}
        <form onSubmit={submit} className="etoe-sign">
          <label className="field">
            Full name of authorised signatory
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name" required />
          </label>
          <label className="etoe-accept">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            I approve and accept these Terms of Engagement on behalf of {ctx.entityName}.
          </label>
          <div className="btn-row">
            <button type="submit" disabled={busy}>
              {busy ? 'Signing.' : 'Approve and submit'}
            </button>
          </div>
          {/*
            Said before the signature, not after. This is the one page where what is
            recorded is part of what makes the signature worth anything, so the signer
            should know it is being recorded at the moment they sign.
          */}
          <p className="field-hint">
            Signing electronically has the same effect as signing on paper. We record your
            name, the date and time, your IP address and your browser as evidence of this
            signature.
          </p>
        </form>
      </div>
    </div>
  );
}
